/**
 * PeriodizationScreen.jsx
 * Mobile App version of Training Periodization
 * Features: Meso/Micro cycles, progression tracking, injury management, AI recommendations
 */
import React, { useState, useEffect } from 'react';
import { 
  Dumbbell, Calendar, Target, TrendingUp, AlertTriangle, 
  ChevronRight, Plus, Trophy, Zap, Activity, RefreshCw,
  ChevronDown, ChevronUp, Sparkles, Clock, BarChart3,
  Shield, Heart, X, Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Phase colors and info
const PHASES = {
  accumulation: { 
    color: 'bg-blue-500/20 border-blue-500/50 text-blue-400', 
    icon: '📈', 
    label: 'Aufbau',
    description: 'Volumen aufbauen'
  },
  intensification: { 
    color: 'bg-orange-500/20 border-orange-500/50 text-orange-400', 
    icon: '🔥', 
    label: 'Intensiv',
    description: 'Intensität steigern'
  },
  realization: { 
    color: 'bg-purple-500/20 border-purple-500/50 text-purple-400', 
    icon: '⭐', 
    label: 'Peak',
    description: 'Höchstleistung'
  },
  deload: { 
    color: 'bg-green-500/20 border-green-500/50 text-green-400', 
    icon: '🧘', 
    label: 'Erholung',
    description: 'Regeneration'
  },
};

// Severity colors
const SEVERITY = {
  mild: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50', label: 'Leicht' },
  moderate: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/50', label: 'Mittel' },
  severe: { color: 'bg-red-500/20 text-red-400 border-red-500/50', label: 'Schwer' },
};

// Body parts for injuries
const BODY_PARTS = [
  'Schulter', 'Rücken', 'Knie', 'Handgelenk', 'Ellbogen', 
  'Hüfte', 'Nacken', 'Knöchel', 'Brust', 'Andere'
];

export const PeriodizationScreen = ({ portalToken, customerId, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [injuries, setInjuries] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  
  // Modal states
  const [showPlanGenerator, setShowPlanGenerator] = useState(false);
  const [showAddInjury, setShowAddInjury] = useState(false);
  const [showLogProgress, setShowLogProgress] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  
  // Plan config
  const [planConfig, setPlanConfig] = useState({
    duration_weeks: 12,
    goal: 'aufbau',
    include_deload: true,
    deload_every_weeks: 4
  });
  
  // New injury
  const [newInjury, setNewInjury] = useState({
    body_part: '',
    severity: 'mild',
    notes: ''
  });
  
  // Progress log
  const [progressLog, setProgressLog] = useState({
    exercise_name: '',
    weight_kg: '',
    reps: '',
    sets: '',
    rpe: ''
  });

  useEffect(() => {
    if (portalToken) {
      loadData();
    }
  }, [portalToken]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [weekRes, injuryRes, recsRes] = await Promise.all([
        axios.get(`${API}/training-sync/periodization/current-week`, {
          headers: { Authorization: `Bearer ${portalToken}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/training-sync/injuries`, {
          headers: { Authorization: `Bearer ${portalToken}` }
        }).catch(() => ({ data: { injuries: [] } })),
        axios.get(`${API}/training-sync/progression/recommendations`, {
          headers: { Authorization: `Bearer ${portalToken}` }
        }).catch(() => ({ data: { recommendations: [] } }))
      ]);

      setCurrentWeek(weekRes.data);
      setInjuries(injuryRes.data?.injuries || []);
      setRecommendations(recsRes.data?.recommendations || []);
    } catch (error) {
      console.error('Failed to load periodization data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePlan = async () => {
    setGeneratingPlan(true);
    try {
      const params = new URLSearchParams(planConfig).toString();
      const response = await axios.post(
        `${API}/training-sync/periodization/generate-plan?${params}`,
        {},
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );

      if (response.data.success) {
        await axios.post(
          `${API}/training-sync/periodization/activate/${response.data.plan.id}`,
          response.data.plan,
          { headers: { Authorization: `Bearer ${portalToken}` } }
        );

        toast.success('Trainingsplan erstellt! 🎉');
        setShowPlanGenerator(false);
        loadData();
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen des Plans');
    } finally {
      setGeneratingPlan(false);
    }
  };

  const addInjury = async () => {
    if (!newInjury.body_part) {
      toast.error('Bitte Körperteil auswählen');
      return;
    }
    try {
      const today = new Date().toISOString().split('T')[0];
      await axios.post(
        `${API}/training-sync/injuries`,
        { ...newInjury, start_date: today },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );

      toast.success('Einschränkung hinzugefügt');
      setShowAddInjury(false);
      setNewInjury({ body_part: '', severity: 'mild', notes: '' });
      loadData();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const removeInjury = async (injuryId) => {
    try {
      await axios.delete(`${API}/training-sync/injuries/${injuryId}`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      toast.success('Einschränkung entfernt');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Entfernen');
    }
  };

  const logProgression = async () => {
    if (!progressLog.exercise_name || !progressLog.weight_kg) {
      toast.error('Bitte Übung und Gewicht eingeben');
      return;
    }
    try {
      const today = new Date().toISOString().split('T')[0];
      await axios.post(
        `${API}/training-sync/progression/log`,
        {
          ...progressLog,
          date: today,
          weight_kg: parseFloat(progressLog.weight_kg),
          reps: parseInt(progressLog.reps) || 0,
          sets: parseInt(progressLog.sets) || 0,
          rpe: progressLog.rpe ? parseFloat(progressLog.rpe) : null
        },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );

      toast.success('Training geloggt! 💪');
      setShowLogProgress(false);
      setProgressLog({ exercise_name: '', weight_kg: '', reps: '', sets: '', rpe: '' });
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const phase = currentWeek?.phase || 'accumulation';
  const phaseInfo = PHASES[phase] || PHASES.accumulation;

  return (
    <div className="p-4 space-y-4 pb-24">
      {/* Header */}
      <div className="flex justify-between items-center pt-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/30 to-purple-500/20 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h1 className="text-white text-base font-bold">Periodisierung</h1>
            <p className="text-zinc-400 text-xs">Trainingsplanung & Zyklen</p>
          </div>
        </div>
        <button 
          onClick={loadData}
          disabled={loading}
          className="p-2 bg-zinc-800 rounded-lg"
          data-testid="refresh-periodization-btn"
        >
          <RefreshCw className={`w-5 h-5 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="bg-zinc-800/50 rounded-2xl p-8 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto mb-3"></div>
          <p className="text-zinc-400 text-sm">Lade Trainingsdaten...</p>
        </div>
      ) : (
        <>
          {/* Current Week / Phase Card */}
          {currentWeek ? (
            <div className={`rounded-2xl p-4 border ${phaseInfo.color}`} data-testid="current-phase-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{phaseInfo.icon}</span>
                  <div>
                    <p className="text-white font-semibold">{phaseInfo.label}-Phase</p>
                    <p className="text-zinc-400 text-xs">{phaseInfo.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold">Woche {currentWeek.week || 1}</p>
                  <p className="text-zinc-400 text-xs">von {currentWeek.total_weeks || 12}</p>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="mt-3">
                <div className="flex justify-between text-xs text-zinc-400 mb-1">
                  <span>Fortschritt</span>
                  <span>{Math.round(((currentWeek.week || 1) / (currentWeek.total_weeks || 12)) * 100)}%</span>
                </div>
                <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all"
                    style={{ width: `${((currentWeek.week || 1) / (currentWeek.total_weeks || 12)) * 100}%` }}
                  />
                </div>
              </div>

              {/* Focus areas */}
              {currentWeek.focus && (
                <div className="mt-3 p-2 bg-zinc-900/50 rounded-lg">
                  <p className="text-xs text-zinc-400 mb-1">Fokus diese Woche:</p>
                  <p className="text-sm text-white">{currentWeek.focus}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-zinc-800/50 rounded-2xl p-6 text-center">
              <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-white font-medium mb-2">Kein aktiver Trainingsplan</p>
              <p className="text-zinc-400 text-sm mb-4">Erstelle einen periodisierten Plan für optimale Ergebnisse</p>
              <button
                onClick={() => setShowPlanGenerator(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 mx-auto"
                data-testid="create-plan-btn"
              >
                <Plus className="w-4 h-4" />
                Plan erstellen
              </button>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setShowLogProgress(true)}
              className="bg-zinc-800/80 rounded-xl p-4 text-left"
              data-testid="log-progress-btn"
            >
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center mb-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
              </div>
              <p className="text-white text-sm font-medium">Progress loggen</p>
              <p className="text-zinc-500 text-xs">Übung eintragen</p>
            </button>
            
            <button
              onClick={() => setShowAddInjury(true)}
              className="bg-zinc-800/80 rounded-xl p-4 text-left"
              data-testid="add-injury-btn"
            >
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center mb-2">
                <Shield className="w-4 h-4 text-orange-400" />
              </div>
              <p className="text-white text-sm font-medium">Einschränkung</p>
              <p className="text-zinc-500 text-xs">Verletzung melden</p>
            </button>
          </div>

          {/* AI Recommendations */}
          {recommendations.length > 0 && (
            <div className="bg-zinc-800/50 rounded-2xl p-4" data-testid="recommendations-section">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <p className="text-white font-medium text-sm">KI-Empfehlungen</p>
              </div>
              <div className="space-y-2">
                {recommendations.slice(0, 3).map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 bg-zinc-900/50 rounded-lg">
                    <Zap className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <p className="text-zinc-300 text-xs">{rec.text || rec}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Injuries */}
          {injuries.length > 0 && (
            <div className="bg-zinc-800/50 rounded-2xl p-4" data-testid="injuries-section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                  <p className="text-white font-medium text-sm">Aktive Einschränkungen</p>
                </div>
                <span className="text-xs text-zinc-400">{injuries.length}</span>
              </div>
              <div className="space-y-2">
                {injuries.map((injury, idx) => {
                  const severityInfo = SEVERITY[injury.severity] || SEVERITY.mild;
                  return (
                    <div 
                      key={injury.id || idx} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${severityInfo.color}`}
                    >
                      <div>
                        <p className="text-white text-sm font-medium">{injury.body_part}</p>
                        <p className="text-zinc-400 text-xs">{severityInfo.label}</p>
                      </div>
                      <button
                        onClick={() => removeInjury(injury.id)}
                        className="p-1.5 hover:bg-white/10 rounded-lg"
                      >
                        <X className="w-4 h-4 text-zinc-400" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Plan Generator Modal */}
      {showPlanGenerator && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end justify-center">
          <div className="bg-zinc-900 rounded-t-3xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-lg">Neuer Trainingsplan</h2>
              <button onClick={() => setShowPlanGenerator(false)} className="p-2">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Duration */}
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Dauer (Wochen)</label>
                <div className="grid grid-cols-4 gap-2">
                  {[8, 12, 16, 20].map(weeks => (
                    <button
                      key={weeks}
                      onClick={() => setPlanConfig(p => ({ ...p, duration_weeks: weeks }))}
                      className={`py-2 rounded-lg text-sm font-medium transition-all ${
                        planConfig.duration_weeks === weeks 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {weeks}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Goal */}
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Trainingsziel</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'aufbau', label: 'Muskelaufbau', icon: '💪' },
                    { id: 'kraft', label: 'Kraft', icon: '🏋️' },
                    { id: 'ausdauer', label: 'Ausdauer', icon: '🏃' },
                    { id: 'definition', label: 'Definition (-20%)', icon: '🎯' },
                    { id: 'recomp', label: 'Recomp (-15%)', icon: '🔄' },
                    { id: 'leichte_definition', label: 'Leicht (-10%)', icon: '✨' }
                  ].map(goal => (
                    <button
                      key={goal.id}
                      onClick={() => setPlanConfig(p => ({ ...p, goal: goal.id }))}
                      className={`py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        planConfig.goal === goal.id 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      <span>{goal.icon}</span>
                      {goal.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Deload */}
              <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <div>
                  <p className="text-white text-sm">Deload-Wochen</p>
                  <p className="text-zinc-500 text-xs">Regenerationswochen einplanen</p>
                </div>
                <button
                  onClick={() => setPlanConfig(p => ({ ...p, include_deload: !p.include_deload }))}
                  className={`w-12 h-6 rounded-full transition-all ${
                    planConfig.include_deload ? 'bg-green-500' : 'bg-zinc-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    planConfig.include_deload ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              {planConfig.include_deload && (
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Deload alle X Wochen</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[3, 4, 5].map(weeks => (
                      <button
                        key={weeks}
                        onClick={() => setPlanConfig(p => ({ ...p, deload_every_weeks: weeks }))}
                        className={`py-2 rounded-lg text-sm font-medium transition-all ${
                          planConfig.deload_every_weeks === weeks 
                            ? 'bg-green-600 text-white' 
                            : 'bg-zinc-800 text-zinc-400'
                        }`}
                      >
                        {weeks} Wochen
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <button
              onClick={generatePlan}
              disabled={generatingPlan}
              className="w-full mt-6 py-3 bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              data-testid="generate-plan-btn"
            >
              {generatingPlan ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Erstelle Plan...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Plan generieren
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Add Injury Modal */}
      {showAddInjury && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end justify-center">
          <div className="bg-zinc-900 rounded-t-3xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-lg">Einschränkung melden</h2>
              <button onClick={() => setShowAddInjury(false)} className="p-2">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Body Part */}
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Körperteil</label>
                <div className="grid grid-cols-3 gap-2">
                  {BODY_PARTS.map(part => (
                    <button
                      key={part}
                      onClick={() => setNewInjury(i => ({ ...i, body_part: part }))}
                      className={`py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                        newInjury.body_part === part 
                          ? 'bg-orange-600 text-white' 
                          : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {part}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Severity */}
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Schweregrad</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(SEVERITY).map(([key, info]) => (
                    <button
                      key={key}
                      onClick={() => setNewInjury(i => ({ ...i, severity: key }))}
                      className={`py-2 rounded-lg text-sm font-medium transition-all border ${
                        newInjury.severity === key 
                          ? info.color 
                          : 'bg-zinc-800 text-zinc-400 border-transparent'
                      }`}
                    >
                      {info.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Notizen (optional)</label>
                <textarea
                  value={newInjury.notes}
                  onChange={(e) => setNewInjury(i => ({ ...i, notes: e.target.value }))}
                  placeholder="Beschreibe die Einschränkung..."
                  className="w-full bg-zinc-800 rounded-lg p-3 text-white text-sm resize-none h-20"
                />
              </div>
            </div>
            
            <button
              onClick={addInjury}
              className="w-full mt-6 py-3 bg-orange-600 text-white rounded-xl font-medium"
              data-testid="save-injury-btn"
            >
              Speichern
            </button>
          </div>
        </div>
      )}

      {/* Log Progress Modal */}
      {showLogProgress && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end justify-center">
          <div className="bg-zinc-900 rounded-t-3xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-lg">Training loggen</h2>
              <button onClick={() => setShowLogProgress(false)} className="p-2">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Exercise Name */}
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Übung</label>
                <input
                  type="text"
                  value={progressLog.exercise_name}
                  onChange={(e) => setProgressLog(p => ({ ...p, exercise_name: e.target.value }))}
                  placeholder="z.B. Bankdrücken"
                  className="w-full bg-zinc-800 rounded-lg p-3 text-white text-sm"
                />
              </div>
              
              {/* Weight, Reps, Sets */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Gewicht (kg)</label>
                  <input
                    type="number"
                    value={progressLog.weight_kg}
                    onChange={(e) => setProgressLog(p => ({ ...p, weight_kg: e.target.value }))}
                    placeholder="80"
                    className="w-full bg-zinc-800 rounded-lg p-3 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Wdh.</label>
                  <input
                    type="number"
                    value={progressLog.reps}
                    onChange={(e) => setProgressLog(p => ({ ...p, reps: e.target.value }))}
                    placeholder="10"
                    className="w-full bg-zinc-800 rounded-lg p-3 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Sätze</label>
                  <input
                    type="number"
                    value={progressLog.sets}
                    onChange={(e) => setProgressLog(p => ({ ...p, sets: e.target.value }))}
                    placeholder="3"
                    className="w-full bg-zinc-800 rounded-lg p-3 text-white text-sm"
                  />
                </div>
              </div>
              
              {/* RPE */}
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">RPE (1-10, optional)</label>
                <div className="flex gap-1">
                  {[6, 7, 8, 9, 10].map(rpe => (
                    <button
                      key={rpe}
                      onClick={() => setProgressLog(p => ({ ...p, rpe: p.rpe === rpe.toString() ? '' : rpe.toString() }))}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                        progressLog.rpe === rpe.toString() 
                          ? 'bg-green-600 text-white' 
                          : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {rpe}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <button
              onClick={logProgression}
              className="w-full mt-6 py-3 bg-green-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              data-testid="save-progress-btn"
            >
              <Check className="w-4 h-4" />
              Training speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodizationScreen;
